import BodyText from '@lyra/core/components/BodyText'
import Table, {
  createColumnHelper,
  TableColumnDef,
  TableData,
  TableMarkerRow,
} from '@lyra/core/components/Table'
import formatUSD from '@lyra/core/utils/formatUSD'
import { MarketId } from '@lyra/web/constants/markets'
import useMarkets from '@lyra/web/hooks/useMarkets'
import { useMemo } from 'react'
import { StackProps } from 'tamagui'

import { TradeOptionTableTickerData } from '.'
import TradeOptionTableRowIndicator from './TradeOptionTableRowIndicator'

export const SINGLE_TICKER_GROUP_ID = 'ticker'

type Props = {
  selectedMarket: MarketId
  columns: TableColumnDef<TradeOptionTableTickerData>[]
  rows: TableData<TradeOptionTableTickerData>[]
  isCalls: boolean
} & StackProps

const TradeOptionTableCallOrPut = ({
  selectedMarket,
  columns: _columns,
  rows,
  isCalls,
  ...tableProps
}: Props) => {
  const columnHelper = createColumnHelper<TradeOptionTableTickerData>()

  const columns = useMemo(
    () => [
      columnHelper.group({
        id: SINGLE_TICKER_GROUP_ID,
        header: isCalls ? 'Calls' : 'Puts',
        columns: [
          columnHelper.accessor('strikePrice', {
            id: 'strikePrice',
            header: 'STRIKE',
            cell: (props) => {
              const strikePrice = props.cell.row.original.strikePrice ?? 0
              const dps = strikePrice >= 100 ? 0 : 2
              return <BodyText>{formatUSD(strikePrice, { showCommas: false, dps })}</BodyText>
            },
          }),
          ..._columns,
        ],
      }),
    ],
    [_columns, columnHelper, isCalls]
  )

  const spotPrice = useMarkets()[selectedMarket].spotPrice

  const spotPriceMarker: TableMarkerRow = useMemo(() => {
    const spotPriceRowIdx = rows.reduce(
      (markerIdx, row) =>
        row.strikePrice && spotPrice < row.strikePrice ? markerIdx : markerIdx + 1,
      0
    )
    return {
      id: 'spot-marker',
      insertIndex: spotPriceRowIdx,
      autoCenter: true,
      content: <TradeOptionTableRowIndicator marketId={selectedMarket} />,
    }
  }, [selectedMarket, rows, spotPrice])

  return (
    <Table
      {...tableProps}
      horizontalCenter
      size="lg"
      data={rows}
      columns={columns}
      markerRow={spotPriceMarker}
    />
  )
}

export default TradeOptionTableCallOrPut
